import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FiArrowLeft, FiDownload, FiSearch } from "react-icons/fi";
import { FaClipboardList, FaTools, FaHeadset } from "react-icons/fa";
import { animatePage } from "../../animation/animationPage";
import NavContext from "../../context/NavContext";
import {
  getAllRequestHistory,
  downloadRequestPdf,
} from "../../_services/requestHistory.service";
import styles from "./RequestHistoryPage.module.scss";

// Icone associee a chaque type de demande.
const TYPE_ICONS = {
  calibration: <FaClipboardList />,
  sav: <FaTools />,
  support: <FaHeadset />,
};

const RequestHistoryPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { ownerId } = useContext(NavContext);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getAllRequestHistory(ownerId);
        if (mounted) setRequests(data);
      } catch (err) {
        if (mounted) setError(t("request_history.load_error"));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (ownerId) load();
    return () => {
      mounted = false;
    };
  }, [ownerId, t]);

  // Applique filtre par type + recherche texte (ref, sujet, materiel...).
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return requests.filter((r) => {
      if (typeFilter !== "all" && r._type !== typeFilter) return false;
      if (!term) return true;
      const haystack = [
        r.ref_number,
        r.type_label,
        r.subject,
        r.material_type,
        r.material_model,
        r.category,
        r.cabine_name,
        r.request_date,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [requests, search, typeFilter]);

  const handleDownload = async (row) => {
    setDownloadingId(row.id + row._type);
    try {
      await downloadRequestPdf(row._type, ownerId, row.id, row.pdf_filename);
    } catch (err) {
      setError(t("request_history.download_error"));
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString();
    } catch (_) {
      return value;
    }
  };

  // Libelle court decrivant la demande selon son type.
  const requestLabel = (row) => {
    if (row._type === "calibration") return row.type_label || row.type;
    if (row._type === "sav")
      return [row.material_type, row.material_model].filter(Boolean).join(" - ");
    if (row._type === "support")
      return row.subject || row.category;
    return "";
  };

  const typeTabs = [
    { key: "all", label: t("request_history.filter_all") },
    { key: "calibration", label: t("request_history.filter_calibration") },
    { key: "sav", label: t("request_history.filter_sav") },
    { key: "support", label: t("request_history.filter_support") },
  ];

  return (
    <motion.div
      className={styles.page}
      variants={animatePage}
      exit="exit"
      initial="hidden"
      animate="visible"
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate("/private/dashboard")}
        >
          <FiArrowLeft /> {t("request_history.back")}
        </button>
        <h1 className={styles.title}>{t("request_history.title")}</h1>
        <p className={styles.subtitle}>{t("request_history.subtitle")}</p>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t("request_history.search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.tabs}>
          {typeTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${styles.tab} ${
                typeFilter === tab.key ? styles.tabActive : ""
              }`}
              onClick={() => setTypeFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.info}>{t("request_history.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className={styles.info}>{t("request_history.empty")}</div>
      ) : (
        <div className={styles.list}>
          {filtered.map((row) => {
            const busy = downloadingId === row.id + row._type;
            return (
              <div key={`${row._type}-${row.id}`} className={styles.card}>
                <span className={styles.cardIcon}>{TYPE_ICONS[row._type]}</span>
                <div className={styles.cardBody}>
                  <div className={styles.cardTop}>
                    <span className={styles.ref}>{row.ref_number}</span>
                    <span className={styles.date}>
                      {formatDate(row.created_at)}
                    </span>
                  </div>
                  <div className={styles.cardLabel}>{requestLabel(row)}</div>
                  {row.cabine_name && (
                    <div className={styles.cardMeta}>{row.cabine_name}</div>
                  )}
                </div>
                <div className={styles.cardAction}>
                  {row.pdf_available ? (
                    <button
                      type="button"
                      className={styles.downloadButton}
                      onClick={() => handleDownload(row)}
                      disabled={busy}
                    >
                      <FiDownload />
                      {busy
                        ? t("request_history.downloading")
                        : t("request_history.download")}
                    </button>
                  ) : (
                    <span className={styles.expired}>
                      {t("request_history.expired")}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default RequestHistoryPage;
